import { Form, Formik, FormikErrors } from "formik";
import { useState } from "react";
import * as yup from "yup";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles
} from "@material-ui/core";

import { useTenant } from "src/components/LoggedInAdminProvider";
import { FormikCombobox } from "src/components/form/Combobox";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import { listAllAdmins_listAdmins } from "src/graphql/generated/listAllAdmins";
import { tenantOwnerPromoteVariables } from "src/graphql/generated/tenantOwnerPromote";
import { getErrorMessage } from "src/util/errors";

type FormValuesAndErrors = tenantOwnerPromoteVariables & {
  saveErrors?: any;
};

const useStyles = makeStyles(() => ({
  dialogContent: {
    "&:first-child": {
      paddingTop: 0,
      marginTop: 0
    }
  }
}));

export default function AddOwnerDialog({
  open,
  onClose,
  onSubmit,
  allAdmins
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: tenantOwnerPromoteVariables) => Promise<void>;
  allAdmins: listAllAdmins_listAdmins[];
}): JSX.Element {
  const [saveErrors, setSaveErrors] = useState<string | null>(null);

  const handleSubmit = async (payload: tenantOwnerPromoteVariables) => {
    setSaveErrors(null);

    try {
      await onSubmit(payload);
    } catch (e) {
      setSaveErrors(getErrorMessage(e));
    }
  };

  return (
    <Dialog open={open}>
      <DialogTitle>Promote Organization Owner</DialogTitle>
      <Formik
        initialValues={{
          adminId: ""
        }}
        validationSchema={yup.object().shape({
          adminId: yup.string().required("Required")
        })}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <AddOwnerForm
            onClose={onClose}
            isSubmitting={isSubmitting}
            saveErrors={saveErrors}
            allAdmins={allAdmins}
          />
        )}
      </Formik>
    </Dialog>
  );
}

function AddOwnerForm({
  isSubmitting,
  onClose,
  saveErrors,
  allAdmins
}: {
  isSubmitting?: boolean;
  onClose: () => void;
  saveErrors: string | null;
  allAdmins: listAllAdmins_listAdmins[];
}) {
  const tenant = useTenant()!;
  const classes = useStyles();

  const options = allAdmins
    .filter(admin => !admin.isTenantOwner)
    .map(admin => ({
      value: admin.id,
      label: `${admin.fullName} (${admin.email})`
    }));

  return (
    <Form>
      <DialogContent className={classes.dialogContent}>
        <p>
          Organization Owner is the highest permission level for this dialer
          organization. Organization Owners can manage Organization Settings,
          create and manage subaccounts, and promote and remove other
          Organization Owners.{" "}
          {tenant.isUsingSubaccounts && (
            <>
              They are also automatically made Owners of all current and future
              subaccounts.
            </>
          )}
        </p>
        <p>
          Select an Owner or Admin
          {tenant.isUsingSubaccounts
            ? " from any subaccount of this dialer organization"
            : ""}{" "}
          to promote them to an Organization Owner.
        </p>
        <FormikErrorBanner
          renderBanner={(errors: FormikErrors<FormValuesAndErrors>) => {
            if (errors.saveErrors) {
              return `Error promoting Organization Owner: ${errors.saveErrors}`;
            }
            return "Please fix the errors indicated below.";
          }}
          additionalErrors={saveErrors}
        />
        <FormikCombobox name="adminId" options={options} label="" />
      </DialogContent>

      <DialogActions>
        <Button disabled={isSubmitting} onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          color="primary"
          variant="contained"
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Form>
  );
}
